class TabbedProductCarousel extends HTMLElement {
  connectedCallback() {
    this.tabsContainer = this.querySelector('[data-tabs]');
    this.querySelectorAll('[data-tab-unit] > [role="tab"]').forEach((tab) => this.tabsContainer?.append(tab));
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = [...this.querySelectorAll('[role="tabpanel"]')];

    if (this.tabsContainer) this.tabsContainer.hidden = this.tabs.length <= 1;

    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);

    // Horizon disables slideshows nested inside another slideshow. Product cards
    // in this section are nested in the outer product carousel, so opt their
    // galleries back in when the global product-card carousel setting is enabled.
    this.productGalleryObserver = new MutationObserver(this.onProductGalleryMutation);
    this.productGalleryObserver.observe(this, {
      attributes: true,
      attributeFilter: ['disabled'],
      subtree: true,
    });
    this.enableProductGallerySliders();
    customElements.whenDefined('slideshow-component').then(() => this.enableProductGallerySliders());

    if (window.Shopify?.designMode) {
      this.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    if (this.tabs[0]) this.activate(this.tabs.find((tab) => tab.classList.contains('is-active'))?.dataset.tabId || this.tabs[0].dataset.tabId);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    this.removeEventListener('shopify:block:select', this.onBlockSelect);
    this.productGalleryObserver?.disconnect();
  }

  onProductGalleryMutation = (mutations) => {
    mutations.forEach(({ target }) => {
      if (target instanceof HTMLElement) this.enableProductGallerySlider(target);
    });
  };

  enableProductGallerySlider(slideshow) {
    if (!slideshow.matches('.card-gallery slideshow-component[data-product-card-carousel="true"]')) return;
    if (slideshow.hasAttribute('disabled')) slideshow.removeAttribute('disabled');
  }

  enableProductGallerySliders(root = this) {
    root
      .querySelectorAll('.card-gallery slideshow-component[data-product-card-carousel="true"]')
      .forEach((slideshow) => this.enableProductGallerySlider(slideshow));
  }

  onClick = (event) => {
    const tab = event.target.closest('[role="tab"]');
    if (tab && this.contains(tab)) this.activate(tab.dataset.tabId);
  };

  onKeydown = (event) => {
    const currentIndex = this.tabs.indexOf(event.target);
    if (currentIndex < 0 || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

    event.preventDefault();
    let nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? this.tabs.length - 1 : currentIndex;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
    this.activate(this.tabs[nextIndex].dataset.tabId, true);
  };

  onBlockSelect = (event) => this.activate(event.detail.blockId);

  async activate(id, focus = false) {
    const activeTab = this.tabs.find((tab) => tab.dataset.tabId === id);
    const activePanel = this.panels.find((panel) => panel.dataset.panelId === id);
    if (!activePanel) return;

    this.currentTabId = id;

    this.tabs.forEach((tab) => {
      const active = tab === activeTab;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });
    this.panels.forEach((panel) => (panel.hidden = panel !== activePanel));

    if (focus) activeTab?.focus();

    if (activePanel.dataset.loaded === 'false' && activePanel.dataset.collectionUrl) {
      await this.loadTabPanel(activePanel);
    }

    if (this.currentTabId === id) {
      requestAnimationFrame(() => {
        this.enableProductGallerySliders(activePanel);
        window.dispatchEvent(new Event('resize'));
      });
    }
  }

  async loadTabPanel(panel) {
    if (panel.dataset.loading === 'true') return;
    panel.dataset.loading = 'true';

    const collectionUrl = panel.dataset.collectionUrl;
    if (!collectionUrl) {
      panel.dataset.loaded = 'true';
      panel.dataset.loading = 'false';
      return;
    }

    const separator = collectionUrl.includes('?') ? '&' : '?';
    const requestUrl = `${collectionUrl}${separator}section_id=tabbed-carousel-panel`;

    try {
      const response = await fetch(requestUrl);
      if (!response.ok) throw new Error(`HTTP error ${response.status}`);
      const text = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/html');

      const targetSection = doc.querySelector('#shopify-section-tabbed-carousel-panel');
      if (targetSection) {
        panel.innerHTML = targetSection.innerHTML;
      } else {
        const fallback = doc.querySelector('.tabbed-product-carousel__carousel, .tabbed-product-carousel__empty');
        if (fallback) {
          panel.innerHTML = '';
          panel.appendChild(fallback);
        }
      }

      panel.dataset.loaded = 'true';
    } catch (error) {
      console.error('Failed to load tab panel:', error);
      const loadingEl = panel.querySelector('.tabbed-product-carousel__loading');
      if (loadingEl) {
        loadingEl.innerHTML = '<p class="tabbed-product-carousel__empty">Unable to load products. Please try again later.</p>';
      }
    } finally {
      panel.dataset.loading = 'false';
    }
  }
}

if (!customElements.get('tabbed-product-carousel')) {
  customElements.define('tabbed-product-carousel', TabbedProductCarousel);
}
